/**
 * Structure de navigation du détail d'une campagne :
 *
 * - « Candidats » : un candidat par ligne, un score par test ; une ligne ouvre
 *   la fiche du candidat en panneau ;
 * - un onglet par TEST, avec ses vues collectives (Résultats, Matching) ;
 * - « Référentiels » : la PRÉPARATION des matchings (attentes du poste, culture
 *   d'entreprise, référents). Ce ne sont pas des résultats : rangés au niveau
 *   des tests, ils se lisaient comme tels.
 *
 * Ce qui est ici est STATIQUE — la liste des onglets ne dépend que de la
 * configuration de la campagne, celle des sections que du test. Ce qui dépend
 * des données (un référentiel est-il posé ?) se décide dans la vue.
 */
import type { TestCampagne } from "@repo/results";

/** Onglets transverses aux tests. */
export type OngletGeneral = "candidats" | "referentiels";
export type OngletCampagne = OngletGeneral | TestCampagne;
export type SectionTest = "resultats" | "matching";
export type CibleReferentiel = "pami" | "pc-fit";

export const LIBELLE_TEST: Record<TestCampagne, string> = {
  inbasket: "In-Basket",
  entstruct: "FairView",
  pami: "PAMI",
  fitculturel: "PC Fit",
  copsoq3: "COPSOQ",
};

export const LIBELLE_SECTION: Record<SectionTest, string> = {
  resultats: "Résultats",
  matching: "Matching",
};

/**
 * Sections qu'un test PEUT porter, dans l'ordre d'affichage. La vue en retire
 * celles qui n'ont encore rien à montrer (Matching sans référentiel posé).
 *
 * L'In-Basket ne produit pas de score global : pas de matching.
 * L'entretien structuré se compare au barème de son template.
 * Le COPSOQ III est un diagnostic, pas une comparaison à un poste : Résultats
 * seuls.
 */
export const SECTIONS_DU_TEST: Record<TestCampagne, readonly SectionTest[]> = {
  inbasket: ["resultats"],
  entstruct: ["resultats", "matching"],
  pami: ["resultats", "matching"],
  fitculturel: ["resultats", "matching"],
  copsoq3: ["resultats"],
};

export const LIBELLE_CIBLE: Record<CibleReferentiel, string> = {
  pami: "Poste (PAMI)",
  "pc-fit": "Culture (PC Fit)",
};

/**
 * Cible de référentiel adossée à un test : les attentes du poste pour le PAMI,
 * la culture d'entreprise pour le fit culturel.
 */
export const CIBLE_DU_TEST: Partial<Record<TestCampagne, CibleReferentiel>> = {
  pami: "pami",
  fitculturel: "pc-fit",
};

const TESTS: readonly TestCampagne[] = [
  "inbasket",
  "entstruct",
  "pami",
  "fitculturel",
  "copsoq3",
];

export function estTest(onglet: string): onglet is TestCampagne {
  return (TESTS as readonly string[]).includes(onglet);
}

/**
 * Tests que la campagne fait passer, dans l'ordre des onglets.
 *
 * La passation candidat fit-culturel porte le template `fit-culturel` — à ne
 * pas confondre avec `referentiel`, conteneur interne des contributions de
 * référents, qui n'est jamais un test de candidat.
 */
export function testsDeLaCampagne(
  config: readonly { typeEval: string; template?: string }[],
): TestCampagne[] {
  const a = (predicat: (e: { typeEval: string; template?: string }) => boolean) =>
    config.some(predicat);
  const tests: TestCampagne[] = [];
  if (a((e) => e.typeEval === "inbasket")) tests.push("inbasket");
  if (a((e) => e.typeEval === "fairview")) tests.push("entstruct");
  if (a((e) => e.typeEval === "form" && e.template === "pami")) tests.push("pami");
  if (a((e) => e.typeEval === "form" && e.template === "pc-fit"))
    tests.push("fitculturel");
  if (a((e) => e.typeEval === "form" && e.template === "copsoq3"))
    tests.push("copsoq3");
  return tests;
}

/** Tests sans score global : leur colonne Candidats montre l'état, pas un score. */
export const SANS_SCORE_GLOBAL: readonly TestCampagne[] = ["inbasket", "copsoq3"];

/** Cibles de référentiel portées par les tests de la campagne, dans l'ordre. */
export function ciblesDeLaCampagne(
  tests: readonly TestCampagne[],
): CibleReferentiel[] {
  return tests.flatMap((t) => {
    const cible = CIBLE_DU_TEST[t];
    return cible ? [cible] : [];
  });
}
